<template>
  <div>
    <!-- Filtreler -->
    <v-row class="mb-3" dense>
      <v-col cols="12" sm="3">
        <v-select
          v-model="filters.type"
          :items="typeItems"
          label="Tür"
          density="compact"
          variant="outlined"
          hide-details
          clearable
          @update:model-value="resetAndLoad"
        />
      </v-col>
      <v-col cols="12" sm="3">
        <v-text-field
          v-model="filters.yasa_no"
          label="Yasa No"
          density="compact"
          variant="outlined"
          hide-details
          clearable
          @keyup.enter="resetAndLoad"
          @click:clear="resetAndLoad"
        />
      </v-col>
      <v-col cols="12" sm="3">
        <v-select
          v-model="filters.mahkeme"
          :items="mahkemeler"
          label="Mahkeme"
          density="compact"
          variant="outlined"
          hide-details
          clearable
          @update:model-value="resetAndLoad"
        />
      </v-col>
      <v-col cols="12" sm="3">
        <v-text-field
          v-model="filters.q"
          label="Arama"
          density="compact"
          variant="outlined"
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          @keyup.enter="resetAndLoad"
          @click:clear="resetAndLoad"
        />
      </v-col>
    </v-row>

    <v-alert v-if="error" type="error" class="mb-3" closable @click:close="error = null">
      {{ error }}
    </v-alert>

    <!-- Toplu seçim araç çubuğu -->
    <v-card v-if="selected.length" class="mb-2 pa-2 d-flex align-center" color="primary" variant="tonal">
      <span class="text-body-2 font-weight-medium mr-3">{{ selected.length }} chunk seçildi</span>
      <v-spacer />
      <v-btn size="small" variant="text" @click="selected = []">Seçimi Temizle</v-btn>
      <v-btn size="small" color="error" variant="flat" class="ml-2" @click="confirmBulkDelete">
        <span class="material-symbols-outlined mr-1" style="font-size:16px">delete</span>
        Seçilenleri Sil
      </v-btn>
    </v-card>

    <!-- Tablo -->
    <v-card>
      <v-data-table
        v-model="selected"
        :headers="headers"
        :items="items"
        :loading="loading"
        :items-per-page="limit"
        item-value="id"
        show-select
        hide-default-footer
        density="compact"
      >
        <template #item.index_type="{ item }">
          <v-chip size="x-small" :color="item.index_type === 'laws' ? 'success' : 'info'" variant="flat">
            {{ item.index_type === 'laws' ? 'Yasa' : 'Karar' }}
          </v-chip>
        </template>

        <template #item.icerik_preview="{ item }">
          <span class="text-caption text-medium-emphasis">{{ item.icerik_preview }}</span>
        </template>

        <template #item.actions="{ item }">
          <v-btn size="x-small" icon variant="plain" @click="openEdit(item)">
            <span class="material-symbols-outlined" style="font-size:16px">edit</span>
          </v-btn>
          <v-btn size="x-small" icon variant="plain" color="error" @click="confirmDelete(item)">
            <span class="material-symbols-outlined" style="font-size:16px">delete</span>
          </v-btn>
        </template>
      </v-data-table>

      <!-- Sayfalama + sayfa başı kayıt -->
      <v-card-text class="d-flex align-center flex-wrap gap-2 pt-2">
        <span class="text-caption text-medium-emphasis">Toplam: {{ total.toLocaleString() }}</span>
        <v-spacer />
        <div class="d-flex align-center">
          <span class="text-caption text-medium-emphasis mr-2">Sayfa başı:</span>
          <v-select
            v-model="limit"
            :items="perPageOptions"
            density="compact"
            variant="outlined"
            hide-details
            style="width:80px"
            @update:model-value="resetAndLoad"
          />
        </div>
        <v-pagination
          v-model="page"
          :length="totalPages"
          :total-visible="5"
          density="compact"
          @update:model-value="load"
        />
      </v-card-text>
    </v-card>

    <!-- Düzenleme dialog -->
    <v-dialog v-model="editDialog" max-width="700">
      <v-card v-if="editItem">
        <v-card-title>Chunk Düzenle — #{{ editItem.id }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="editItem.baslik" label="Başlık" variant="outlined" density="compact" class="mb-2" />
          <v-textarea v-model="editItem.icerik" label="İçerik" variant="outlined" rows="8" auto-grow />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="editDialog = false">İptal</v-btn>
          <v-btn color="primary" :loading="saving" @click="saveEdit">Kaydet</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Tekli silme onay dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title>Chunk Sil</v-card-title>
        <v-card-text>
          <strong>#{{ deleteTarget?.id }}</strong> numaralı chunk silinecek. Bu işlem geri alınamaz.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="deleteDialog = false">İptal</v-btn>
          <v-btn color="error" :loading="deleting" @click="doDelete">Sil</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Toplu silme onay dialog -->
    <v-dialog v-model="bulkDeleteDialog" max-width="440">
      <v-card>
        <v-card-title>Toplu Silme</v-card-title>
        <v-card-text>
          <strong>{{ selected.length }}</strong> chunk kalıcı olarak silinecek. Bu işlem geri alınamaz.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="bulkDeleteDialog = false">İptal</v-btn>
          <v-btn color="error" :loading="bulkDeleting" @click="doBulkDelete">Sil</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import axios from '@/plugins/axios'

const API_BASE = process.env.VUE_APP_API_BASE_URL as string

const headers = [
  { title: 'ID',     key: 'id',             width: '60px' },
  { title: 'Tür',    key: 'index_type',      width: '80px' },
  { title: 'Dosya',  key: 'dosya_adi',       width: '150px' },
  { title: 'Yasa No',key: 'yasa_no',         width: '100px' },
  { title: 'Madde',  key: 'madde_no',        width: '70px' },
  { title: 'Başlık', key: 'baslik',          width: '180px' },
  { title: 'İçerik', key: 'icerik_preview',  sortable: false },
  { title: '',       key: 'actions',         sortable: false, width: '80px' },
]

const typeItems = [
  { title: 'Yasa / Tüzük', value: 'laws' },
  { title: 'Mahkeme Kararı', value: 'cases' },
]

const perPageOptions = [10, 20, 50, 100, 200]

const filters   = ref({ type: '', yasa_no: '', mahkeme: '', q: '' })
const mahkemeler = ref<string[]>([])
const items     = ref<any[]>([])
const total     = ref(0)
const page      = ref(1)
const limit     = ref(20)
const loading   = ref(false)
const error     = ref<string | null>(null)
const selected  = ref<number[]>([])

const totalPages = computed(() => Math.ceil(total.value / limit.value) || 1)

const editDialog   = ref(false)
const editItem     = ref<any>(null)
const saving       = ref(false)

const deleteDialog  = ref(false)
const deleteTarget  = ref<any>(null)
const deleting      = ref(false)

const bulkDeleteDialog = ref(false)
const bulkDeleting     = ref(false)

async function load() {
  loading.value = true
  error.value   = null
  try {
    const params: any = { page: page.value, limit: limit.value }
    if (filters.value.type)    params.type    = filters.value.type
    if (filters.value.yasa_no) params.yasa_no = filters.value.yasa_no
    if (filters.value.mahkeme) params.mahkeme = filters.value.mahkeme
    if (filters.value.q)       params.q       = filters.value.q

    const res = await axios.get(`${API_BASE}/admin/parser/chunks`, { params })
    items.value    = res.data.items
    total.value    = res.data.total
    selected.value = []
  } catch (e: any) {
    error.value = e?.response?.data?.error ?? e?.message ?? 'Hata'
  } finally {
    loading.value = false
  }
}

function resetAndLoad() {
  page.value = 1
  load()
}

async function loadMahkemeler() {
  try {
    const res = await axios.get(`${API_BASE}/admin/parser/mahkemeler`)
    mahkemeler.value = res.data.mahkemeler
  } catch { /* ignore */ }
}

function openEdit(item: any) {
  editItem.value = { ...item }
  editDialog.value = true
}

async function saveEdit() {
  saving.value = true
  try {
    await axios.patch(`${API_BASE}/admin/parser/chunks/${editItem.value.id}`, {
      baslik: editItem.value.baslik,
      icerik: editItem.value.icerik,
    })
    editDialog.value = false
    load()
  } catch (e: any) {
    error.value = e?.response?.data?.error ?? e?.message ?? 'Kaydetme hatası'
  } finally {
    saving.value = false
  }
}

function confirmDelete(item: any) {
  deleteTarget.value = item
  deleteDialog.value = true
}

async function doDelete() {
  deleting.value = true
  try {
    await axios.delete(`${API_BASE}/admin/parser/chunks/${deleteTarget.value.id}`)
    deleteDialog.value = false
    load()
  } catch (e: any) {
    error.value = e?.response?.data?.error ?? e?.message ?? 'Silme hatası'
  } finally {
    deleting.value = false
  }
}

function confirmBulkDelete() {
  bulkDeleteDialog.value = true
}

async function doBulkDelete() {
  bulkDeleting.value = true
  try {
    await axios.delete(`${API_BASE}/admin/parser/chunks/bulk`, {
      data: { ids: selected.value },
    })
    bulkDeleteDialog.value = false
    load()
  } catch (e: any) {
    error.value = e?.response?.data?.error ?? e?.message ?? 'Toplu silme hatası'
  } finally {
    bulkDeleting.value = false
  }
}

onMounted(() => {
  load()
  loadMahkemeler()
})
</script>
